import {ReactNode, useState} from "react";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    Grid,
    Link as MuiLink,
    Stack,
    Typography
} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import MergeIcon from "@mui/icons-material/Merge";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import HotelIcon from "@mui/icons-material/Hotel";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StarIcon from "@mui/icons-material/Star";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import {DeduplicationTaskService} from "crqlar-react-common/dist/services/profile/deduplicationTask.service";
import StaysTable from "../common/StaysTable";

interface Props {
    profile: Profile;
    onUnmerged?: () => void;
}

const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return "-";
    return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'EUR'}).format(value);
};

const InfoRow = ({label, value, valueColor}: { label: string; value: ReactNode; valueColor?: string; }) =>
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight="medium" color={valueColor} textAlign="right">{value}</Typography>
    </Box>;

const StatTile = ({label, value}: { label: string; value: ReactNode; }) =>
    <Grid item xs={6} md={3}>
        <Box sx={{p: 2, borderRadius: 1, bgcolor: 'grey.50', height: '100%'}}>
            <Typography variant="h6" fontWeight="medium">{value}</Typography>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
    </Grid>;

const ProfileSmartData = ({profile, onUnmerged}: Props) => {
    const {t} = useTranslation();
    const [unmerging, setUnmerging] = useState(false);

    const revenue = profile.smartData?.revenueStats;
    const stays = profile.smartData?.stays;

    return (
        <Grid container spacing={3}>
            {/* Merge Status Card */}
            <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{height: '100%'}}>
                    <CardHeader
                        avatar={<MergeIcon/>}
                        title={t('Merge Status')}
                        titleTypographyProps={{variant: 'h6'}}
                        action={profile.lastSmartDataCalc &&
                            <Typography variant="caption" color="text.secondary" sx={{mr: 2}}>
                                {t('Last calculated')}: {formatDateDMYHM(profile.lastSmartDataCalc)}
                            </Typography>
                        }/>

                    <CardContent>
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">{t("Status")}</Typography>
                                <Box mt={0.5}>
                                    <Chip
                                        label={profile.isMergeProduct ? t('Merged Profile') : t('Original Profile')}
                                        color={profile.isMergeProduct ? 'info' : 'default'}
                                        icon={profile.isMergeProduct ? <MergeIcon/> : undefined}/>
                                </Box>
                            </Box>

                            {profile.clusterId &&
                                <Box>
                                    <Typography variant="caption" color="text.secondary">{t("Cluster ID")}</Typography>
                                    <Typography variant="body2">{profile.clusterId}</Typography>
                                </Box>
                            }

                            {profile.mainProfileId &&
                                <Box>
                                    <Typography variant="caption"
                                                color="text.secondary">{t("Main Profile")}</Typography>
                                    <Box>
                                        <MuiLink
                                            component={Link}
                                            to={`/system/profiles/${profile.mainProfileId}`}
                                            underline="hover">
                                            {profile.mainProfileId}
                                        </MuiLink>
                                    </Box>
                                </Box>
                            }

                            {profile.mergedChildrenIds && profile.mergedChildrenIds.length > 0 &&
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {t("Merged from")} ({profile.mergedChildrenIds.length})
                                    </Typography>
                                    <Stack direction="row" flexWrap="wrap" gap={1} mt={0.5}>
                                        {profile.mergedChildrenIds.map((childId) =>
                                            <Chip
                                                key={childId}
                                                label={childId.substring(0, 8) + '...'}
                                                size="small"
                                                component={Link}
                                                to={`/system/profiles/${childId}?followMerge=false`}
                                                clickable
                                                variant="outlined"/>
                                        )}
                                    </Stack>
                                </Box>
                            }

                            {profile.isMergeProduct && profile.id &&
                                <Box>
                                    <LoadingButton
                                        variant="outlined"
                                        color="warning"
                                        size="small"
                                        loading={unmerging}
                                        startIcon={<CallSplitIcon/>}
                                        onClick={async () => {
                                            setUnmerging(true);
                                            try {
                                                await DeduplicationTaskService.unmergeByProfileIds([profile.id!]);
                                                onUnmerged?.();
                                            } finally {
                                                setUnmerging(false);
                                            }
                                        }}>
                                        {t('Unmerge')}
                                    </LoadingButton>
                                </Box>
                            }
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            {/* Cluster Info Card */}
            <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{height: '100%'}}>
                    <CardHeader
                        avatar={<AccountTreeIcon/>}
                        title={t('Clustering')}
                        titleTypographyProps={{variant: 'h6'}}/>

                    <CardContent>
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">{t("Cluster Status")}</Typography>
                                <Box mt={0.5}>
                                    <Chip
                                        label={profile.clusterId ? t('In Cluster') : t('Not Clustered')}
                                        color={profile.clusterId ? 'primary' : 'default'}
                                        size="small"/>
                                </Box>
                            </Box>

                            {profile.mainProfileId &&
                                <Box>
                                    <Typography variant="caption" color="text.secondary">{t("Role")}</Typography>
                                    <Box mt={0.5}>
                                        <Chip label={t('Child Profile')} size="small" color="warning"/>
                                    </Box>
                                </Box>
                            }

                            {profile.mergedChildrenIds && profile.mergedChildrenIds.length > 0 &&
                                <Box>
                                    <Typography variant="caption" color="text.secondary">{t("Role")}</Typography>
                                    <Box mt={0.5}>
                                        <Chip label={t('Main Profile')} size="small" color="success"/>
                                    </Box>
                                </Box>
                            }
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            {/* Stays Overview — flat table */}
            <Grid item xs={12}>
                <Card variant="outlined">
                    <CardHeader
                        avatar={<HotelIcon/>}
                        title={t('Stays Overview')}
                        titleTypographyProps={{variant: 'h6'}}/>

                    <CardContent>
                        <StaysTable
                            current={profile.currentStay}
                            active={profile.activeStays}
                            upcoming={profile.upcomingStays}
                            past={profile.pastStays}/>
                    </CardContent>
                </Card>
            </Grid>

            {/* Smart Analytics — headline stats + breakdown */}
            {profile.smartData &&
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardHeader
                            avatar={<TrendingUpIcon/>}
                            title={t('Smart Analytics')}
                            titleTypographyProps={{variant: 'h6'}}
                            action={profile.smartData.isVip &&
                                <Chip icon={<StarIcon/>} label={t('VIP')} color="warning" size="small" sx={{mr: 2}}/>
                            }/>

                        <CardContent>
                            <Stack spacing={3}>
                                <Grid container spacing={2}>
                                    <StatTile label={t('Total Revenue')} value={formatCurrency(revenue?.totalRevenue)}/>
                                    <StatTile label={t('Avg per Stay')}
                                              value={formatCurrency(revenue?.averageRevenuePerStay)}/>
                                    <StatTile label={t('Total Stays')} value={stays?.totalStays ?? '-'}/>
                                    <StatTile label={t('Total Nights')} value={stays?.totalNights ?? '-'}/>
                                </Grid>

                                <Grid container spacing={3}>
                                    {revenue &&
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                {t('Revenue breakdown')}
                                            </Typography>
                                            <Stack spacing={1}>
                                                <InfoRow label={t('Avg per Night')}
                                                         value={formatCurrency(revenue.averageRevenuePerNight)}/>
                                                <Divider sx={{my: 0.5}}/>
                                                <InfoRow label={t('Room Revenue')}
                                                         value={formatCurrency(revenue.totalRoomRevenue)}/>
                                                <InfoRow label={t('Service Revenue')}
                                                         value={formatCurrency(revenue.totalServiceRevenue)}/>
                                                <InfoRow label={t('F&B Revenue')}
                                                         value={formatCurrency(revenue.totalFBRevenue)}/>
                                                <InfoRow label={t('Spa Revenue')}
                                                         value={formatCurrency(revenue.totalSpaRevenue)}/>
                                            </Stack>
                                        </Grid>}

                                    {stays &&
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                {t('Stay Patterns')}
                                            </Typography>
                                            <Stack spacing={1}>
                                                <InfoRow
                                                    label={t('Avg Stay Duration')}
                                                    value={`${stays.averageStayDuration ?? '-'} ${t('nights')}`}/>
                                                <Divider sx={{my: 0.5}}/>
                                                <InfoRow label={t('First Stay')} value={stays.firstStayDate ?? '-'}/>
                                                <InfoRow label={t('Last Stay')} value={stays.lastStayDate ?? '-'}/>
                                                {stays.nextStayDate &&
                                                    <InfoRow label={t('Next Stay')} value={stays.nextStayDate}
                                                             valueColor="primary"/>}
                                            </Stack>
                                        </Grid>}

                                    {!!profile.smartData.interests?.length &&
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                {t('Interests')}
                                            </Typography>
                                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                                {profile.smartData.interests.map((interest, index) =>
                                                    <Chip key={index} label={interest.interest} size="small"
                                                          variant="outlined"/>
                                                )}
                                            </Stack>
                                        </Grid>}

                                    {!!profile.smartData.categories?.length &&
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                {t('Categories')}
                                            </Typography>
                                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                                {profile.smartData.categories.map((category, index) =>
                                                    <Chip
                                                        key={index}
                                                        label={category.text || category.code}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"/>
                                                )}
                                            </Stack>
                                        </Grid>}
                                </Grid>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>}
        </Grid>
    );
};

export default ProfileSmartData;
